// src/components/RateManagement.jsx
import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { menuPortalTarget, menuPosition, themedSelectStyles } from "../utils/reactSelectTheme";
import ToastContainer, { useToast } from "./common/Toast";
import { useConfirm } from "./common/useConfirm";
import SeasonalRatesPanel from "./common/SeasonalRatesPanel";
import {
  fetchRateCalendar,
  updateBaseNightlyRate,
  updateCompareAtRate,
  bulkUpdateRates,
  pushCategoryRateToEzee,
} from "../services/RateService";
import "../css/theme.css";
import "../css/components.css";
import "./ManageResorts.css"; // reuses .resorts-page-container/.vt-page-header - same shared look as PaymentAccounts.jsx
import "./RateManagement.css";

const BULK_MODE_OPTIONS = [
  { value: "FLAT", label: "Set to a flat rate" },
  { value: "PERCENT", label: "Adjust by a percentage" },
];

const WINDOW_SIZE_DAYS = 14;
const NAV_STEP_DAYS = 7;
const PUSH_WINDOW_DAYS = 365;
const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Local-date helpers - deliberately not toISOString()/Date math across UTC,
// which shifts the calendar by a day depending on the viewer's timezone
// offset. Everything here reasons in the browser's own local midnight.
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (date, n) => {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
};
const formatDate = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const isWeekend = (date) => date.getDay() === 0 || date.getDay() === 6;

const RateManagement = () => {
  const [windowStart, setWindowStart] = useState(startOfToday);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toasts, showToast, dismissToast } = useToast();
  const { confirm, ConfirmDialogElement } = useConfirm();

  const [resortFilter, setResortFilter] = useState(null); // null = all
  const [mappedOnlyFilter, setMappedOnlyFilter] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkMode, setBulkMode] = useState("FLAT");
  const [bulkValue, setBulkValue] = useState("");
  const [isApplyingBulk, setIsApplyingBulk] = useState(false);

  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingDraft, setEditingDraft] = useState("");
  // Separate editing state for the "Compare-At" column - independent of
  // Base Rate's above, so editing one never clobbers the other's draft.
  const [editingCompareAtCategoryId, setEditingCompareAtCategoryId] = useState(null);
  const [editingCompareAtDraft, setEditingCompareAtDraft] = useState("");
  // Which row's SeasonalRatesPanel is expanded, and (if opened by clicking a
  // calendar cell rather than the "Seasonal Rates" button) which date to
  // prefill the add-row with.
  const [expandedCategoryId, setExpandedCategoryId] = useState(null);
  const [expandedInitialDate, setExpandedInitialDate] = useState(null);
  const [pushingCategoryId, setPushingCategoryId] = useState(null);

  const days = useMemo(
    () => Array.from({ length: WINDOW_SIZE_DAYS }, (_, i) => addDays(windowStart, i)),
    [windowStart]
  );

  const loadRows = async () => {
    try {
      // Only show the full-page "Loading..." placeholder on the very first
      // load - a refresh after editing a seasonal rate (same expanded panel
      // still open) shouldn't unmount the table/panel out from under the
      // user just because rows are being re-fetched.
      if (rows.length === 0) setLoading(true);
      const from = formatDate(windowStart);
      const toExclusive = formatDate(addDays(windowStart, WINDOW_SIZE_DAYS));
      const data = await fetchRateCalendar(from, toExclusive);
      setRows(data || []);
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to load rates", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowStart]);

  const resortOptions = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => seen.set(r.resortId, r.resortName));
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }));
  }, [rows]);

  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (resortFilter && r.resortId !== resortFilter.value) return false;
      if (mappedOnlyFilter && !r.ezeeMapped) return false;
      return true;
    });
  }, [rows, resortFilter, mappedOnlyFilter]);

  const toggleSelect = (categoryId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      const visibleCategoryIds = visibleRows.map((r) => r.categoryId);
      const allSelected = visibleCategoryIds.length > 0 && visibleCategoryIds.every((id) => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        visibleCategoryIds.forEach((id) => next.delete(id));
        return next;
      }
      return new Set([...prev, ...visibleCategoryIds]);
    });
  };

  const startEdit = (row) => {
    setEditingCategoryId(row.categoryId);
    setEditingDraft(row.baseNightlyRate ?? "");
  };

  const saveEdit = async (categoryId) => {
    const value = editingDraft === "" ? null : parseFloat(editingDraft);
    try {
      await updateBaseNightlyRate(categoryId, value);
      setEditingCategoryId(null);
      showToast("Rate updated", "success");
      await loadRows();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update rate", "danger");
    }
  };

  const startEditCompareAt = (row) => {
    setEditingCompareAtCategoryId(row.categoryId);
    setEditingCompareAtDraft(row.compareAtRate ?? "");
  };

  const saveEditCompareAt = async (categoryId) => {
    const value = editingCompareAtDraft === "" ? null : parseFloat(editingCompareAtDraft);
    try {
      await updateCompareAtRate(categoryId, value);
      setEditingCompareAtCategoryId(null);
      showToast("Compare-at price updated", "success");
      await loadRows();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Failed to update compare-at price", "danger");
    }
  };

  const openSeasonalPanel = (row, date) => {
    const isSameRow = expandedCategoryId === row.categoryId;
    const isSameDate = expandedInitialDate === date;
    if (isSameRow && (date === undefined || isSameDate)) {
      setExpandedCategoryId(null);
      setExpandedInitialDate(null);
      return;
    }
    setExpandedCategoryId(row.categoryId);
    setExpandedInitialDate(date || null);
  };

  const handleBulkApply = async () => {
    if (selectedIds.size === 0) {
      return showToast("Select at least one row first", "warning");
    }
    if (bulkValue === "" || Number.isNaN(parseFloat(bulkValue))) {
      return showToast("Enter a value to apply", "warning");
    }

    const label = bulkMode === "FLAT" ? `set to ₹${bulkValue}` : `adjusted by ${bulkValue}%`;
    const ok = await confirm({
      title: "Apply bulk rate change?",
      message: `This will update ${selectedIds.size} room categor${selectedIds.size === 1 ? "y" : "ies"}, ${label}. This also pushes to eZee for any that are mapped.`,
      confirmLabel: "Apply",
    });
    if (!ok) return;

    setIsApplyingBulk(true);
    try {
      const results = await bulkUpdateRates({
        categoryIds: Array.from(selectedIds),
        mode: bulkMode,
        value: parseFloat(bulkValue),
      });
      const appliedCount = results.filter((r) => r.applied).length;
      const skippedCount = results.length - appliedCount;
      showToast(
        skippedCount === 0
          ? `Updated ${appliedCount} room categor${appliedCount === 1 ? "y" : "ies"}`
          : `Updated ${appliedCount}, skipped ${skippedCount} (see console for reasons)`,
        skippedCount === 0 ? "success" : "warning"
      );
      if (skippedCount > 0) {
        console.warn("Bulk rate update - skipped rows:", results.filter((r) => !r.applied));
      }
      setSelectedIds(new Set());
      setBulkValue("");
      await loadRows();
    } catch (err) {
      console.error(err);
      showToast(err.message || "Bulk update failed", "danger");
    } finally {
      setIsApplyingBulk(false);
    }
  };

  const handlePushToEzee = async (row) => {
    setPushingCategoryId(row.categoryId);
    try {
      const today = formatDate(startOfToday());
      const to = formatDate(addDays(startOfToday(), PUSH_WINDOW_DAYS));
      const result = await pushCategoryRateToEzee(row.categoryId, today, to);
      const pushed = result?.pushed || [];
      showToast(
        pushed.length === 0 ? "Nothing to push - no rate configured for this category" : pushed.join("; "),
        pushed.some((p) => p.startsWith("[DRY RUN]")) ? "warning" : "success"
      );
    } catch (err) {
      console.error(err);
      showToast(err.message || "Push to eZee failed", "danger");
    } finally {
      setPushingCategoryId(null);
    }
  };

  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.categoryId));
  const totalColumns = 7 + WINDOW_SIZE_DAYS; // +1 for the Compare-At column

  return (
    <div className="resorts-page-container rate-management-page" style={{ padding: "0px 20px 20px 20px", boxSizing: "border-box" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
      {ConfirmDialogElement}

      <div className="vt-page-header">
        <h2>Rate Management</h2>
      </div>

      <div className="rate-management-toolbar">
        <div className="rate-management-filters">
          <Select
            options={resortOptions}
            value={resortFilter}
            onChange={setResortFilter}
            isClearable
            placeholder="All resorts"
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
          <label className="rate-management-checkbox-label">
            <input
              type="checkbox"
              checked={mappedOnlyFilter}
              onChange={(e) => setMappedOnlyFilter(e.target.checked)}
            />
            eZee-mapped only
          </label>
        </div>

        <div className="rate-management-date-nav">
          <button type="button" className="vt-btn vt-btn-purple" onClick={() => setWindowStart((d) => addDays(d, -NAV_STEP_DAYS))}>
            ← Previous
          </button>
          <span className="rate-management-date-range">
            {days[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })}
            {" – "}
            {days[days.length - 1].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </span>
          <button type="button" className="vt-btn vt-btn-purple" onClick={() => setWindowStart(startOfToday())}>
            Today
          </button>
          <button type="button" className="vt-btn vt-btn-purple" onClick={() => setWindowStart((d) => addDays(d, NAV_STEP_DAYS))}>
            Next →
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="rate-management-bulk-bar">
          <span>{selectedIds.size} selected</span>
          <Select
            options={BULK_MODE_OPTIONS}
            value={BULK_MODE_OPTIONS.find((o) => o.value === bulkMode)}
            onChange={(selected) => setBulkMode(selected.value)}
            isSearchable={false}
            menuPortalTarget={menuPortalTarget}
            menuPosition={menuPosition}
            styles={themedSelectStyles()}
          />
          <input
            type="number"
            placeholder={bulkMode === "FLAT" ? "New rate (₹)" : "% change (e.g. 10 or -5)"}
            value={bulkValue}
            onChange={(e) => setBulkValue(e.target.value)}
          />
          <button type="button" className="vt-btn vt-btn-primary" disabled={isApplyingBulk} onClick={handleBulkApply}>
            {isApplyingBulk ? "Applying..." : "Apply"}
          </button>
          <button type="button" className="vt-btn vt-btn-purple" onClick={() => setSelectedIds(new Set())}>
            Clear selection
          </button>
        </div>
      )}

      {loading ? (
        <p>Loading rates...</p>
      ) : (
        <div className="rate-management-calendar-wrapper">
          <table className="rate-management-calendar">
            <thead>
              <tr>
                <th className="rate-management-sticky-col">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} />
                </th>
                <th className="rate-management-sticky-col rate-management-sticky-col--name">Property / Category</th>
                <th>Rooms</th>
                <th>Base Rate</th>
                <th title="Optional 'was' price shown struck through on the guest site - purely a display signal, never charged">Compare-At</th>
                <th>eZee</th>
                <th>Push</th>
                {days.map((date) => (
                  <th key={formatDate(date)} className={isWeekend(date) ? "rate-management-weekend" : ""}>
                    <div className="rate-management-day-header">
                      <span>{WEEKDAY_LABELS[date.getDay()]}</span>
                      <strong>{date.getDate()}</strong>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={totalColumns} style={{ textAlign: "center", padding: "20px" }}>
                    {rows.length === 0 ? "No room categories found" : "No rows match the current filters"}
                  </td>
                </tr>
              ) : (
                visibleRows.map((row) => (
                  <React.Fragment key={row.categoryId}>
                    <tr>
                      <td className="rate-management-sticky-col">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row.categoryId)}
                          onChange={() => toggleSelect(row.categoryId)}
                        />
                      </td>
                      <td className="rate-management-sticky-col rate-management-sticky-col--name">
                        <div className="rate-management-resort-name">{row.resortName}</div>
                        <div className="rate-management-category-name">{row.categoryName}</div>
                      </td>
                      <td>{row.totalRooms}</td>
                      <td>
                        {editingCategoryId === row.categoryId ? (
                          <div className="rate-management-inline-edit">
                            <input
                              type="number"
                              min="0"
                              autoFocus
                              value={editingDraft}
                              onChange={(e) => setEditingDraft(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && saveEdit(row.categoryId)}
                            />
                            <button type="button" className="vt-btn vt-btn-primary" onClick={() => saveEdit(row.categoryId)}>
                              Save
                            </button>
                            <button type="button" className="vt-btn vt-btn-purple" onClick={() => setEditingCategoryId(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="rate-management-rate-value" onClick={() => startEdit(row)} title="Click to edit">
                            {row.baseNightlyRate != null ? `₹${row.baseNightlyRate}` : "Not set"}
                          </span>
                        )}
                      </td>
                      <td>
                        {editingCompareAtCategoryId === row.categoryId ? (
                          <div className="rate-management-inline-edit">
                            <input
                              type="number"
                              min="0"
                              autoFocus
                              value={editingCompareAtDraft}
                              onChange={(e) => setEditingCompareAtDraft(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && saveEditCompareAt(row.categoryId)}
                            />
                            <button type="button" className="vt-btn vt-btn-primary" onClick={() => saveEditCompareAt(row.categoryId)}>
                              Save
                            </button>
                            <button type="button" className="vt-btn vt-btn-purple" onClick={() => setEditingCompareAtCategoryId(null)}>
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="rate-management-rate-value" onClick={() => startEditCompareAt(row)} title="Click to edit - shown struck through on the guest site">
                            {row.compareAtRate != null ? `₹${row.compareAtRate}` : "Not set"}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={row.ezeeMapped ? "rate-management-badge rate-management-badge--mapped" : "rate-management-badge"}>
                          {row.ezeeMapped ? "Mapped" : "Not mapped"}
                        </span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="vt-btn vt-btn-primary"
                          disabled={!row.ezeeMapped || pushingCategoryId === row.categoryId}
                          title={row.ezeeMapped ? "Push current rate to eZee" : "Set eZee Room Type ID + Rate Type ID first"}
                          onClick={() => handlePushToEzee(row)}
                        >
                          {pushingCategoryId === row.categoryId ? "..." : "Push"}
                        </button>
                      </td>
                      {row.days.map((day) => {
                        const date = new Date(day.date);
                        const cellClasses = [
                          "rate-management-cell",
                          day.override ? "rate-management-cell--override" : "",
                          day.rate == null ? "rate-management-cell--empty" : "",
                          isWeekend(date) ? "rate-management-weekend" : "",
                        ].filter(Boolean).join(" ");
                        return (
                          <td
                            key={day.date}
                            className={cellClasses}
                            onClick={() => openSeasonalPanel(row, day.date)}
                            title={day.override ? "Seasonal override - click to manage" : "Click to add a seasonal override starting here"}
                          >
                            {day.rate != null ? `₹${day.rate}` : "—"}
                          </td>
                        );
                      })}
                    </tr>
                    {expandedCategoryId === row.categoryId && (
                      <tr>
                        <td colSpan={totalColumns} className="rate-management-seasonal-cell">
                          <SeasonalRatesPanel
                            // Remounts (resetting the add-row draft) when a
                            // different date cell is clicked on the same
                            // already-open row - initialFromDate only seeds
                            // useState's initial value, which otherwise
                            // wouldn't update on a prop change alone.
                            key={`${row.categoryId}-${expandedInitialDate}`}
                            categoryId={row.categoryId}
                            showToast={showToast}
                            initialFromDate={expandedInitialDate}
                            onRatesChanged={loadRows}
                            onClose={() => {
                              setExpandedCategoryId(null);
                              setExpandedInitialDate(null);
                            }}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RateManagement;
